/**
 * AdminAnalyticsPage.jsx
 * 관리자 인사이트 페이지 - 기존 admin_analytics.html 1:1 이관
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Chart from 'chart.js/auto';
import Header from '../components/layout/Header';
import api from '../services/api';

// KPI 카드 컴포넌트 - 미니멀 리디자인
function KPICard({ icon, iconColor, label, value, sublabel, subvalue }) {
    const isPositive = subvalue?.includes('+');
    return (
        <div className="glass-panel rounded-2xl p-5 group hover:bg-white/[0.06] transition-all duration-300">
            <div className="flex items-start justify-between mb-4">
                <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <i className={`${icon} ${iconColor} text-lg`}></i>
                </div>
                {isPositive && (
                    <span className="text-[10px] text-emerald-400 font-mono flex items-center gap-1">
                        <i className="fas fa-arrow-up"></i>{subvalue}
                    </span>
                )}
            </div>
            <div>
                <p className="text-[10px] text-slate-500 uppercase font-bold tracking-wider mb-1">{label}</p>
                <h3 className="text-2xl font-bold text-white">{value}</h3>
                <p className="text-xs text-slate-500 mt-1">{sublabel}</p>
            </div>
        </div>
    );
}

// 사용자 상세 모달
function UserDetailModal({ user, isOpen, onClose }) {
    const radarChartRef = useRef(null);
    const radarChartInstance = useRef(null);

    useEffect(() => {
        if (!isOpen || !user || !radarChartRef.current) return;

        // 차트 렌더링
        const ctx = radarChartRef.current.getContext('2d');
        const stats = user.stats || {};

        if (radarChartInstance.current) {
            radarChartInstance.current.destroy();
        }

        radarChartInstance.current = new Chart(ctx, {
            type: 'radar',
            data: {
                labels: ['문법', '논리', '문제해결', '코드품질', '꾸준함'],
                datasets: [{
                    label: '역량',
                    data: [
                        stats.syntax || 50,
                        stats.logic || 50,
                        stats.problem_solving || stats.solving || 50,
                        stats.code_quality || stats.quality || 50,
                        stats.persistence || 50
                    ],
                    backgroundColor: 'rgba(139, 92, 246, 0.25)',
                    borderColor: '#8b5cf6',
                    pointBackgroundColor: '#8b5cf6',
                    borderWidth: 2
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    r: {
                        angleLines: { color: 'rgba(148, 163, 184, 0.1)' },
                        grid: { color: 'rgba(148, 163, 184, 0.1)' },
                        pointLabels: { color: '#cbd5e1', font: { size: 10 } },
                        ticks: { display: false },
                        suggestedMin: 0,
                        suggestedMax: 100
                    }
                },
                plugins: { legend: { display: false } }
            }
        });

        return () => {
            if (radarChartInstance.current) {
                radarChartInstance.current.destroy();
            }
        };
    }, [isOpen, user]);

    if (!isOpen || !user) return null;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 flex items-center justify-center p-4">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="glass-panel rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
            >
                {/* 모달 헤더 */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                            <i className="fas fa-user text-white text-xl"></i>
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">{user.nickname || user.username}</h3>
                            <p className="text-xs text-slate-400">@{user.username}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-slate-400 hover:text-white transition-colors">
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                {/* 모달 바디 */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {/* 기본 정보 */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="glass-panel rounded-xl p-4">
                            <p className="text-xs text-slate-500 uppercase mb-1">레벨</p>
                            <p className="text-2xl font-bold text-cyan-400">Lv.{user.level || 1}</p>
                        </div>
                        <div className="glass-panel rounded-xl p-4">
                            <p className="text-xs text-slate-500 uppercase mb-1">해결 문제</p>
                            <p className="text-2xl font-bold text-emerald-400">{(user.total_solved || 0).toLocaleString()}</p>
                        </div>
                        <div className="glass-panel rounded-xl p-4">
                            <p className="text-xs text-slate-500 uppercase mb-1">주력 언어</p>
                            <p className="text-lg font-semibold text-purple-400">{user.main_language || 'Python'}</p>
                        </div>
                        <div className="glass-panel rounded-xl p-4">
                            <p className="text-[10px] text-slate-500 uppercase font-bold tracking-wider mb-1">마지막 활동</p>
                            <p className="text-sm text-white font-mono">{user.last_active_at || '-'}</p>
                        </div>
                    </div>

                    {/* 역량 레이더 차트 */}
                    <div className="glass-panel rounded-xl p-4">
                        <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-3">역량 분포</h4>
                        <div className="h-48">
                            <canvas ref={radarChartRef}></canvas>
                        </div>
                    </div>

                    {/* 마스터한 스킬 */}
                    <div>
                        <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-3">마스터한 스킬</h4>
                        <div className="flex flex-wrap gap-1.5">
                            {(user.mastered_skills || []).length > 0 ? (
                                user.mastered_skills.map((skill, i) => (
                                    <span key={i} className="text-[10px] px-2 py-1 rounded bg-white/5 text-slate-400">
                                        {skill}
                                    </span>
                                ))
                            ) : (
                                <span className="text-[10px] text-slate-600">아직 마스터한 스킬이 없습니다.</span>
                            )}
                        </div>
                    </div>
                </div>

                {/* 모달 푸터 */}
                <div className="p-4 border-t border-white/5">
                    <button
                        onClick={onClose}
                        className="w-full py-2.5 bg-white/5 hover:bg-white/10 text-white rounded-xl text-sm font-medium transition-colors"
                    >
                        닫기
                    </button>
                </div>
            </motion.div>
        </div>
    );
}

export default function AdminAnalyticsPage() {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [kpi, setKpi] = useState({});
    const [users, setUsers] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedUser, setSelectedUser] = useState(null);
    const pageSize = 10;

    // 토큰 사용량 상태
    const [tokenPeriod, setTokenPeriod] = useState('30d');
    const [tokenData, setTokenData] = useState(null);
    const [tokenLoading, setTokenLoading] = useState(false);

    // 조직 차트 데이터 (isLoading 해제 후 렌더링을 위해 state로 관리)
    const [orgChartData, setOrgChartData] = useState(null);
    const tokenBarChartRef = useRef(null);
    const tokenLineChartRef = useRef(null);
    const tokenBarChartInstance = useRef(null);
    const tokenLineChartInstance = useRef(null);

    // Chart refs
    const radarChartRef = useRef(null);
    const doughnutChartRef = useRef(null);
    const radarChartInstance = useRef(null);
    const doughnutChartInstance = useRef(null);

    // 데이터 로드
    const loadData = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            const authData = await api.checkAuth();
            if (!authData.authenticated) {
                navigate('/login');
                return;
            }
            if (!authData.user?.is_admin) {
                alert('관리자 권한이 필요합니다.');
                navigate('/');
                return;
            }

            // Admin Analytics API 호출
            const data = await api.getAdminAnalytics();

            // KPI 설정
            setKpi(data.kpi || {});

            // 사용자 목록
            setAllUsers(data.users || []);
            setUsers(data.users || []);

            // 차트 데이터 저장 (isLoading 해제 후 useEffect에서 렌더링)
            if (data.charts) {
                setOrgChartData(data.charts);
            }

            setIsLoading(false);
        } catch (err) {
            console.error('Analytics data load failed:', err);
            setError(err.message || '데이터를 불러올 수 없습니다.');
            setIsLoading(false);
        }
    }, [navigate]);

    // 토큰 사용량 로드
    const loadTokenData = useCallback(async (period) => {
        setTokenLoading(true);
        try {
            const data = await api.getAdminTokenUsage({ period });
            setTokenData(data);
        } catch (err) {
            console.error('Token usage load failed:', err);
        } finally {
            setTokenLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
        loadTokenData('30d');
        return () => {
            if (radarChartInstance.current) radarChartInstance.current.destroy();
            if (doughnutChartInstance.current) doughnutChartInstance.current.destroy();
            if (tokenBarChartInstance.current) tokenBarChartInstance.current.destroy();
            if (tokenLineChartInstance.current) tokenLineChartInstance.current.destroy();
        };
    }, [loadData, loadTokenData]);

    // 기간 변경 시 토큰 데이터 재로드 및 차트 갱신
    useEffect(() => {
        loadTokenData(tokenPeriod);
    }, [tokenPeriod, loadTokenData]);

    // 조직 레이더/도넛 차트 렌더링 (isLoading 해제 후 canvas가 DOM에 마운트된 시점에 실행)
    useEffect(() => {
        if (isLoading || !orgChartData) return;
        renderRadarChart(orgChartData.org_radar);
        renderDoughnutChart(orgChartData.lang_dist);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, orgChartData]);

    // 토큰 차트 렌더링
    useEffect(() => {
        if (!tokenData || isLoading) return;

        // 일별 라인 차트
        if (tokenLineChartRef.current) {
            if (tokenLineChartInstance.current) tokenLineChartInstance.current.destroy();
            const dailyData = tokenData.by_day || [];
            tokenLineChartInstance.current = new Chart(tokenLineChartRef.current.getContext('2d'), {
                type: 'line',
                data: {
                    labels: dailyData.map(d => d.date),
                    datasets: [
                        {
                            label: '입력 토큰',
                            data: dailyData.map(d => d.input_tokens),
                            borderColor: '#22d3ee',
                            backgroundColor: 'rgba(34,211,238,0.08)',
                            fill: true,
                            tension: 0.4,
                            borderWidth: 2,
                            pointRadius: 3,
                        },
                        {
                            label: '출력 토큰',
                            data: dailyData.map(d => d.output_tokens),
                            borderColor: '#a855f7',
                            backgroundColor: 'rgba(168,85,247,0.08)',
                            fill: true,
                            tension: 0.4,
                            borderWidth: 2,
                            pointRadius: 3,
                        },
                        {
                            label: 'Thinking 토큰',
                            data: dailyData.map(d => d.thinking_tokens),
                            borderColor: '#f59e0b',
                            backgroundColor: 'rgba(245,158,11,0.08)',
                            fill: true,
                            tension: 0.4,
                            borderWidth: 2,
                            pointRadius: 3,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: { mode: 'index', intersect: false },
                    plugins: { legend: { position: 'bottom', labels: { color: '#94a3b8', usePointStyle: true, font: { size: 11 } } } },
                    scales: {
                        x: { ticks: { color: '#64748b', font: { size: 10 } }, grid: { color: 'rgba(148,163,184,0.07)' } },
                        y: { ticks: { color: '#64748b', font: { size: 10 } }, grid: { color: 'rgba(148,163,184,0.07)' } },
                    },
                },
            });
        }

        // 계정별 바 차트 (상위 10명)
        if (tokenBarChartRef.current) {
            if (tokenBarChartInstance.current) tokenBarChartInstance.current.destroy();
            const top10 = (tokenData.by_user || []).slice(0, 10);
            tokenBarChartInstance.current = new Chart(tokenBarChartRef.current.getContext('2d'), {
                type: 'bar',
                data: {
                    labels: top10.map(u => u.nickname || u.user_id),
                    datasets: [
                        {
                            label: '입력',
                            data: top10.map(u => u.input_tokens),
                            backgroundColor: 'rgba(34,211,238,0.7)',
                        },
                        {
                            label: '출력',
                            data: top10.map(u => u.output_tokens),
                            backgroundColor: 'rgba(168,85,247,0.7)',
                        },
                        {
                            label: 'Thinking',
                            data: top10.map(u => u.thinking_tokens),
                            backgroundColor: 'rgba(245,158,11,0.7)',
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { position: 'bottom', labels: { color: '#94a3b8', usePointStyle: true, font: { size: 11 } } } },
                    scales: {
                        x: { stacked: true, ticks: { color: '#64748b', font: { size: 10 } }, grid: { color: 'rgba(148,163,184,0.07)' } },
                        y: { stacked: true, ticks: { color: '#64748b', font: { size: 10 } }, grid: { color: 'rgba(148,163,184,0.07)' } },
                    },
                },
            });
        }
    }, [tokenData, isLoading]);

    // 검색 필터
    useEffect(() => {
        if (!searchQuery) {
            setUsers(allUsers);
        } else {
            const q = searchQuery.toLowerCase();
            setUsers(allUsers.filter(u =>
                (u.username || '').toLowerCase().includes(q) ||
                (u.name || '').toLowerCase().includes(q) ||
                (u.nickname || '').toLowerCase().includes(q)
            ));
        }
        setCurrentPage(1);
    }, [searchQuery, allUsers]);

    // 조직 레이더 차트
    const renderRadarChart = (radarData) => {
        if (!radarChartRef.current) return;
        const ctx = radarChartRef.current.getContext('2d');
        const values = radarData || [50, 50, 50, 50, 50];

        if (radarChartInstance.current) radarChartInstance.current.destroy();

        radarChartInstance.current = new Chart(ctx, {
            type: 'radar',
            data: {
                labels: ['문법 이해', '논리 구성', '문제 해결', '코드 품질', '학습 꾸준함'],
                datasets: [
                    {
                        label: '조직 평균',
                        data: values,
                        backgroundColor: 'rgba(34, 211, 238, 0.2)',
                        borderColor: '#22d3ee',
                        pointBackgroundColor: '#22d3ee',
                        borderWidth: 2
                    },
                    {
                        label: '목표 역량',
                        data: [80, 85, 80, 90, 85],
                        backgroundColor: 'transparent',
                        borderColor: 'rgba(148, 163, 184, 0.3)',
                        borderDash: [5, 5],
                        pointRadius: 0,
                        borderWidth: 1
                    }
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    r: {
                        angleLines: { color: 'rgba(148, 163, 184, 0.1)' },
                        grid: { color: 'rgba(148, 163, 184, 0.1)' },
                        pointLabels: { color: '#cbd5e1', font: { size: 12 } },
                        ticks: { display: false },
                        suggestedMin: 0,
                        suggestedMax: 100
                    }
                },
                plugins: {
                    legend: { position: 'bottom', labels: { color: '#94a3b8', usePointStyle: true } }
                }
            }
        });
    };

    // 언어 분포 도넛 차트
    const renderDoughnutChart = (langData) => {
        if (!doughnutChartRef.current) return;
        const ctx = doughnutChartRef.current.getContext('2d');
        const labels = langData ? Object.keys(langData) : [];
        const data = langData ? Object.values(langData) : [];

        if (labels.length === 0) {
            labels.push('데이터 없음');
            data.push(1);
        }

        if (doughnutChartInstance.current) doughnutChartInstance.current.destroy();

        doughnutChartInstance.current = new Chart(ctx, {
            type: 'doughnut',
            data: {
                labels,
                datasets: [{
                    data,
                    backgroundColor: labels[0] === '데이터 없음' ? ['#334155'] : ['#3b82f6', '#f59e0b', '#ef4444', '#10b981', '#8b5cf6'],
                    borderWidth: 0,
                    hoverOffset: 4
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                cutout: '75%',
                plugins: {
                    legend: {
                        position: 'right',
                        labels: { color: '#94a3b8', usePointStyle: true, boxWidth: 8, font: { size: 11 } }
                    }
                }
            }
        });
    };

    // 페이지네이션
    const totalPages = Math.ceil(users.length / pageSize);
    const startIdx = (currentPage - 1) * pageSize;
    const pageUsers = users.slice(startIdx, startIdx + pageSize);

    // 언어 아이콘 매핑
    const getLangIcon = (lang) => {
        if (!lang) return 'python';
        const l = lang.toLowerCase();
        if (l.includes('py')) return 'python';
        if (l.includes('js') || l.includes('script')) return 'js';
        if (l.includes('java')) return 'java';
        if (l.includes('html')) return 'html5';
        return 'code';
    };

    // 점수 색상
    const getScoreColor = (score) => {
        if (score >= 80) return 'text-emerald-400';
        if (score >= 60) return 'text-cyan-400';
        if (score >= 40) return 'text-yellow-400';
        return 'text-slate-400';
    };

    // 로그아웃
    const handleLogout = async () => {
        try {
            await api.logout();
            navigate('/login');
        } catch (err) {
            console.error('로그아웃 실패:', err);
            navigate('/login');
        }
    };

    return (
        <div className="h-screen bg-[#0B0E14] text-slate-200 flex flex-col font-sans overflow-hidden">
            {/* Background - Grid + Aurora Blobs */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
                <div className="aurora-blob blob-1"></div>
                <div className="aurora-blob blob-2"></div>
                <div className="aurora-blob blob-3"></div>
                <div className="bg-grid-pattern"></div>
            </div>

            {/* 헤더 */}
            <Header
                adminMode={true}
                onNavigate={navigate}
                onLogout={handleLogout}
            />

            {/* 메인 컨텐츠 - 스크롤 가능 */}
            <main className="flex-1 relative z-10 overflow-y-auto p-6 custom-scrollbar">
                <div className="max-w-7xl mx-auto space-y-6 pb-8">

                    {/* 로딩 상태 */}
                    {isLoading && (
                        <div className="flex flex-col items-center justify-center py-20">
                            <div className="w-16 h-16 border-4 border-cyan-500/20 border-t-cyan-500 rounded-full animate-spin mb-4"></div>
                            <p className="text-slate-400">분석 데이터를 불러오는 중...</p>
                        </div>
                    )}

                    {/* 에러 상태 */}
                    {error && !isLoading && (
                        <div className="bg-[#0f1219] border border-white/10 rounded-2xl p-8 text-center">
                            <i className="fas fa-exclamation-triangle text-4xl text-red-500 mb-4"></i>
                            <h3 className="text-lg font-semibold text-white mb-2">데이터를 불러올 수 없습니다</h3>
                            <p className="text-slate-400 mb-4">{error}</p>
                            <button onClick={loadData} className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 rounded text-white text-sm transition-colors">
                                <i className="fas fa-redo mr-1"></i>다시 시도
                            </button>
                        </div>
                    )}

                    {/* 메인 콘텐츠 */}
                    {!isLoading && !error && (
                        <>
                            {/* KPI 카드 */}
                            <section className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <KPICard
                                    icon="fas fa-users"
                                    iconColor="text-blue-400"
                                    label="Total Learners"
                                    value={(kpi.total_users || 0).toLocaleString()}
                                    sublabel={`이번 달 신규 ${kpi.new_users_this_month || 0}명`}
                                    subvalue={`+${kpi.new_users_this_month || 0}`}
                                />
                                <KPICard
                                    icon="fas fa-user-clock"
                                    iconColor="text-emerald-400"
                                    label="Active Today"
                                    value={(kpi.active_today || 0).toLocaleString()}
                                    sublabel="오늘 학습 중인 사용자"
                                    subvalue=""
                                />
                                <KPICard
                                    icon="fas fa-layer-group"
                                    iconColor="text-purple-400"
                                    label="Avg. Skill Level"
                                    value={`Lv. ${kpi.avg_level || 1}`}
                                    sublabel={`평균 ${Math.round(kpi.avg_xp || 0).toLocaleString()} XP`}
                                    subvalue=""
                                />
                                <KPICard
                                    icon="fas fa-trophy"
                                    iconColor="text-amber-400"
                                    label="Solved Problems"
                                    value={(kpi.total_solved || 0).toLocaleString()}
                                    sublabel="누적 해결 문제 수"
                                    subvalue=""
                                />
                            </section>

                            {/* 차트 섹션 */}
                            <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                {/* 레이더 차트 */}
                                <div className="glass-panel rounded-2xl p-6 lg:col-span-2 flex flex-col">
                                    <div className="flex justify-between items-start mb-4">
                                        <div>
                                            <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">조직 역량 분포</h3>
                                            <p className="text-sm text-white mt-1">5대 핵심 역량 평균치</p>
                                        </div>
                                    </div>
                                    <div className="h-[300px]">
                                        <canvas ref={radarChartRef}></canvas>
                                    </div>
                                </div>

                                {/* 도넛 차트 */}
                                <div className="glass-panel rounded-2xl p-6 flex flex-col">
                                    <div className="mb-4">
                                        <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">기술 스택 분포</h3>
                                        <p className="text-sm text-white mt-1">주력 학습 언어</p>
                                    </div>
                                    <div className="h-[250px] flex items-center justify-center relative">
                                        <canvas ref={doughnutChartRef}></canvas>
                                        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                                            <span className="text-3xl font-bold text-white font-mono">{users.length}</span>
                                            <span className="text-[10px] text-slate-500 uppercase tracking-wider">Users</span>
                                        </div>
                                    </div>
                                </div>
                            </section>

                            {/* 사용자 테이블 */}
                            <section className="glass-panel rounded-2xl overflow-hidden">
                                <div className="p-5 border-b border-white/5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                    <div>
                                        <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">구성원 역량 매트릭스</h3>
                                        <p className="text-sm text-white mt-1">개인별 역량 점수 및 보유 스킬</p>
                                    </div>
                                    <div className="relative">
                                        <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs"></i>
                                        <input
                                            type="text"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            placeholder="이름 또는 ID 검색..."
                                            className="bg-slate-800/50 border border-slate-700 text-slate-200 text-xs rounded-lg pl-9 pr-4 py-2 focus:outline-none focus:border-cyan-500 w-48 transition-colors"
                                        />
                                    </div>
                                </div>

                                <div className="overflow-x-auto">
                                    <table className="w-full text-left border-collapse">
                                        <thead className="bg-slate-800/50 text-xs uppercase text-slate-400 font-semibold">
                                            <tr>
                                                <th className="px-6 py-4">구성원 정보</th>
                                                <th className="px-6 py-4">주력 언어</th>
                                                <th className="px-6 py-4 text-center">종합 역량 점수</th>
                                                <th className="px-6 py-4">보유 핵심 스킬</th>
                                                <th className="px-6 py-4 text-right">최근 학습일</th>
                                                <th className="px-6 py-4 text-center">관리</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-800/50 text-sm">
                                            {pageUsers.length === 0 ? (
                                                <tr>
                                                    <td colSpan="6" className="px-6 py-8 text-center text-slate-500">
                                                        데이터가 없습니다.
                                                    </td>
                                                </tr>
                                            ) : (
                                                pageUsers.map((user) => {
                                                    const stats = user.stats || {};
                                                    const avgScore = Math.round(
                                                        ((stats.syntax || 50) + (stats.logic || 50) + (stats.problem_solving || stats.solving || 50) + (stats.code_quality || stats.quality || 50)) / 4
                                                    );
                                                    const scoreColor = getScoreColor(avgScore);

                                                    return (
                                                        <motion.tr
                                                            key={user.id || user.username}
                                                            initial={{ opacity: 0 }}
                                                            animate={{ opacity: 1 }}
                                                            className="border-b border-white/5 hover:bg-white/[0.02] transition-colors group"
                                                        >
                                                            <td className="px-6 py-4">
                                                                <div className="flex items-center gap-3">
                                                                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500/20 to-purple-500/20 flex items-center justify-center text-xs font-bold text-slate-300 border border-white/10">
                                                                        {(user.username || 'U').substring(0, 2).toUpperCase()}
                                                                    </div>
                                                                    <div>
                                                                        <div className="font-medium text-white text-sm">{user.nickname || user.username}</div>
                                                                        <div className="text-[10px] text-slate-500 font-mono">@{user.username}</div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4">
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-slate-300 text-sm">{user.main_language || 'Python'}</span>
                                                                    <span className="text-[10px] text-slate-600 font-mono">Lv.{user.level || 1}</span>
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4 text-center">
                                                                <div className="inline-flex flex-col items-center">
                                                                    <span className={`text-lg font-bold font-mono ${scoreColor}`}>{avgScore}</span>
                                                                    <div className="w-12 h-0.5 bg-white/10 rounded-full overflow-hidden mt-1">
                                                                        <div className={`h-full ${scoreColor.replace('text-', 'bg-')}`} style={{ width: `${avgScore}%` }}></div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4">
                                                                <div className="flex flex-wrap gap-1">
                                                                    {(user.mastered_skills || []).slice(0, 3).map((skill, i) => (
                                                                        <span key={i} className="text-[10px] px-2 py-0.5 rounded bg-white/5 text-slate-400">
                                                                            {skill}
                                                                        </span>
                                                                    ))}
                                                                    {(user.mastered_skills || []).length > 3 && (
                                                                        <span className="text-[10px] text-slate-600">+{user.mastered_skills.length - 3}</span>
                                                                    )}
                                                                    {(user.mastered_skills || []).length === 0 && (
                                                                        <span className="text-slate-600 text-[10px]">-</span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4 text-right text-slate-400 text-xs">
                                                                {user.last_active_at || '-'}
                                                            </td>
                                                            <td className="px-6 py-4 text-center">
                                                                <button
                                                                    onClick={() => setSelectedUser(user)}
                                                                    className="text-slate-500 hover:text-cyan-400 transition-colors"
                                                                    title="상세 보기"
                                                                >
                                                                    <i className="fas fa-ellipsis-h"></i>
                                                                </button>
                                                            </td>
                                                        </motion.tr>
                                                    );
                                                })
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* 페이지네이션 */}
                                <div className="px-6 py-4 border-t border-slate-700/50 flex justify-between items-center text-xs text-slate-500">
                                    <span>Showing {startIdx + 1} to {Math.min(startIdx + pageSize, users.length)} of {users.length} entries</span>
                                    {totalPages > 1 && (
                                        <div className="flex gap-1">
                                            <button
                                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                                disabled={currentPage === 1}
                                                className="px-3 py-1 bg-slate-800 rounded hover:bg-slate-700 disabled:opacity-50"
                                            >
                                                Prev
                                            </button>
                                            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map(page => (
                                                <button
                                                    key={page}
                                                    onClick={() => setCurrentPage(page)}
                                                    className={`px-3 py-1 rounded ${currentPage === page ? 'bg-cyan-900/30 text-cyan-400 border border-cyan-500/30' : 'bg-slate-800 hover:bg-slate-700'}`}
                                                >
                                                    {page}
                                                </button>
                                            ))}
                                            <button
                                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                                disabled={currentPage === totalPages}
                                                className="px-3 py-1 bg-slate-800 rounded hover:bg-slate-700 disabled:opacity-50"
                                            >
                                                Next
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </section>

                            {/* ===== 토큰 사용량 섹션 ===== */}
                            <section className="space-y-4">
                                {/* 섹션 헤더 + 기간 필터 */}
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                    <div>
                                        <h2 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">AI 토큰 사용량</h2>
                                        <p className="text-sm text-white mt-1">계정별 · 기간별 소모량 분석</p>
                                    </div>
                                    <div className="flex gap-1">
                                        {['7d', '30d', '90d', 'all'].map(p => (
                                            <button
                                                key={p}
                                                onClick={() => setTokenPeriod(p)}
                                                className={`px-3 py-1.5 rounded-lg text-xs font-mono transition-colors ${tokenPeriod === p ? 'bg-cyan-900/40 text-cyan-400 border border-cyan-500/40' : 'bg-white/5 text-slate-400 hover:bg-white/10'}`}
                                            >
                                                {p === 'all' ? '전체' : p === '7d' ? '7일' : p === '30d' ? '30일' : '90일'}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {tokenLoading ? (
                                    <div className="glass-panel rounded-2xl p-10 flex items-center justify-center">
                                        <div className="w-8 h-8 border-2 border-cyan-500/20 border-t-cyan-500 rounded-full animate-spin"></div>
                                    </div>
                                ) : tokenData ? (
                                    <>
                                        {/* 토큰 KPI 카드 */}
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                            <div className="glass-panel rounded-2xl p-5">
                                                <p className="text-[10px] text-slate-500 uppercase font-bold tracking-wider mb-1">총 토큰 소모</p>
                                                <h3 className="text-2xl font-bold text-white font-mono">{((tokenData.kpi?.total_tokens || 0) / 1000).toFixed(1)}K</h3>
                                                <p className="text-xs text-slate-500 mt-1">{(tokenData.kpi?.total_calls || 0).toLocaleString()} 회 호출</p>
                                            </div>
                                            <div className="glass-panel rounded-2xl p-5">
                                                <p className="text-[10px] text-slate-500 uppercase font-bold tracking-wider mb-1">입력 토큰</p>
                                                <h3 className="text-2xl font-bold text-cyan-400 font-mono">{((tokenData.kpi?.total_input_tokens || 0) / 1000).toFixed(1)}K</h3>
                                                <p className="text-xs text-slate-500 mt-1">프롬프트 전송량</p>
                                            </div>
                                            <div className="glass-panel rounded-2xl p-5">
                                                <p className="text-[10px] text-slate-500 uppercase font-bold tracking-wider mb-1">출력 토큰</p>
                                                <h3 className="text-2xl font-bold text-purple-400 font-mono">{((tokenData.kpi?.total_output_tokens || 0) / 1000).toFixed(1)}K</h3>
                                                <p className="text-xs text-slate-500 mt-1">AI 응답 생성량</p>
                                            </div>
                                            <div className="glass-panel rounded-2xl p-5">
                                                <p className="text-[10px] text-slate-500 uppercase font-bold tracking-wider mb-1">Thinking 토큰</p>
                                                <h3 className="text-2xl font-bold text-amber-400 font-mono">{((tokenData.kpi?.total_thinking_tokens || 0) / 1000).toFixed(1)}K</h3>
                                                <p className="text-xs text-slate-500 mt-1">추론 내부 연산량</p>
                                            </div>
                                        </div>

                                        {/* 차트 영역 */}
                                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                            {/* 일별 라인 차트 */}
                                            <div className="glass-panel rounded-2xl p-6 lg:col-span-2">
                                                <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">일별 토큰 추이</h3>
                                                <p className="text-sm text-white mb-4">기간별 입출력 토큰 흐름</p>
                                                <div className="h-[220px]">
                                                    {(tokenData.by_day || []).length > 0
                                                        ? <canvas ref={tokenLineChartRef}></canvas>
                                                        : <div className="h-full flex items-center justify-center text-slate-600 text-sm">데이터가 없습니다</div>
                                                    }
                                                </div>
                                            </div>

                                            {/* 기능별 파이 */}
                                            <div className="glass-panel rounded-2xl p-6">
                                                <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">기능별 토큰 비중</h3>
                                                <p className="text-sm text-white mb-4">API 엔드포인트 분포</p>
                                                <div className="space-y-2">
                                                    {Object.entries(tokenData.by_endpoint || {})
                                                        .sort((a, b) => b[1] - a[1])
                                                        .map(([ep, tokens]) => {
                                                            const total = tokenData.kpi?.total_tokens || 1;
                                                            const pct = Math.round((tokens / total) * 100);
                                                            const epLabel = { chat: '채팅', curriculum: '커리큘럼', lesson_plan: '레슨 플랜', grading: '코드 채점', feedback: '피드백', roadmap: '로드맵' }[ep] || ep;
                                                            return (
                                                                <div key={ep}>
                                                                    <div className="flex justify-between text-xs text-slate-400 mb-1">
                                                                        <span>{epLabel}</span>
                                                                        <span className="font-mono">{pct}% · {(tokens / 1000).toFixed(1)}K</span>
                                                                    </div>
                                                                    <div className="w-full h-1.5 bg-white/5 rounded-full overflow-hidden">
                                                                        <div className="h-full bg-gradient-to-r from-cyan-500 to-purple-500 rounded-full" style={{ width: `${pct}%` }}></div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    {Object.keys(tokenData.by_endpoint || {}).length === 0 && (
                                                        <p className="text-slate-600 text-xs">데이터가 없습니다</p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* 계정별 바 차트 */}
                                        <div className="glass-panel rounded-2xl p-6">
                                            <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">계정별 토큰 소모 (상위 10명)</h3>
                                            <p className="text-sm text-white mb-4">사용자별 누적 토큰 사용량</p>
                                            <div className="h-[220px]">
                                                {(tokenData.by_user || []).length > 0
                                                    ? <canvas ref={tokenBarChartRef}></canvas>
                                                    : <div className="h-full flex items-center justify-center text-slate-600 text-sm">데이터가 없습니다</div>
                                                }
                                            </div>
                                        </div>

                                        {/* 계정별 상세 테이블 */}
                                        <div className="glass-panel rounded-2xl overflow-hidden">
                                            <div className="p-5 border-b border-white/5">
                                                <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">계정별 토큰 상세</h3>
                                                <p className="text-sm text-white mt-1">전체 사용자 토큰 소모 내역</p>
                                            </div>
                                            <div className="overflow-x-auto">
                                                <table className="w-full text-left">
                                                    <thead className="bg-slate-800/50 text-xs uppercase text-slate-400 font-semibold">
                                                        <tr>
                                                            <th className="px-6 py-3">사용자</th>
                                                            <th className="px-6 py-3 text-right">입력</th>
                                                            <th className="px-6 py-3 text-right">출력</th>
                                                            <th className="px-6 py-3 text-right">Thinking</th>
                                                            <th className="px-6 py-3 text-right">합계</th>
                                                            <th className="px-6 py-3 text-right">호출 수</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-slate-800/50 text-sm">
                                                        {(tokenData.by_user || []).length === 0 ? (
                                                            <tr><td colSpan="6" className="px-6 py-8 text-center text-slate-500">데이터가 없습니다</td></tr>
                                                        ) : (
                                                            (tokenData.by_user || []).map(u => (
                                                                <tr key={u.user_id} className="hover:bg-white/[0.02] transition-colors">
                                                                    <td className="px-6 py-3">
                                                                        <div className="font-medium text-white text-sm">{u.nickname || u.user_id}</div>
                                                                        <div className="text-[10px] text-slate-500 font-mono">@{u.user_id}</div>
                                                                    </td>
                                                                    <td className="px-6 py-3 text-right text-cyan-400 font-mono text-xs">{(u.input_tokens || 0).toLocaleString()}</td>
                                                                    <td className="px-6 py-3 text-right text-purple-400 font-mono text-xs">{(u.output_tokens || 0).toLocaleString()}</td>
                                                                    <td className="px-6 py-3 text-right text-amber-400 font-mono text-xs">{(u.thinking_tokens || 0).toLocaleString()}</td>
                                                                    <td className="px-6 py-3 text-right text-white font-mono text-xs font-bold">{(u.total_tokens || 0).toLocaleString()}</td>
                                                                    <td className="px-6 py-3 text-right text-slate-400 font-mono text-xs">{(u.call_count || 0).toLocaleString()}</td>
                                                                </tr>
                                                            ))
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </>
                                ) : null}
                            </section>

                            {/* 마지막 업데이트 */}
                            <div className="text-center text-xs text-slate-500">
                                <i className="fas fa-sync-alt mr-1"></i>
                                마지막 업데이트: {new Date().toLocaleString('ko-KR')}
                            </div>
                        </>
                    )}
                </div>
            </main>

            {/* 사용자 상세 모달 */}
            <AnimatePresence>
                {selectedUser && (
                    <UserDetailModal
                        user={selectedUser}
                        isOpen={!!selectedUser}
                        onClose={() => setSelectedUser(null)}
                    />
                )}
            </AnimatePresence>
        </div>
    );
}
